import { FC, useState, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import {
  AWARD_STATUS_OPERATION,
  AwardStatusEnum,
  UPDATE_AWARD_STATUS,
  WalletAwardListRes,
} from 'src/app/modules/pruwallet/types/award-type';
import PruTable from 'src/app/common/components/PruTable/PruTable';
import { getDefaultDisplayDate } from 'src/app/common/utils';
import { WALLET_BASE_PATH, WALLET_AWARD_PATH, WALLET_ASSIGNED_AWARD_PATH } from 'src/app/modules/pruwallet/constants';
import _ from 'lodash';
import { useWalletAwardPermission } from '../../hook/useWalletAwardPermission';
import { AuthenticationState } from '@pruforce/common-adminweb-sdk';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';

type WalletAwardListProps = {
  isLoading: boolean;
  listRes?: WalletAwardListRes;
  onRefresh: () => void;
  onChangePage: (page: number, rowsPerPage: number) => void;
  onUpdatedAwardStatus?: (
    id: string,
    param: UPDATE_AWARD_STATUS,
    operation: AWARD_STATUS_OPERATION,
  ) => Promise<void> | void;
};

const WalletAwardList: FC<WalletAwardListProps> = ({
  isLoading,
  listRes,
  onRefresh,
  onChangePage,
  onUpdatedAwardStatus,
}) => {
  const history = useHistory();
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const { user } = useSelector<RootState, AuthenticationState>((state) => state.auth);

  const { updatePermission, createPermission, approvePermission, viewAssignedPermission } = useWalletAwardPermission();

  const isEditMode = (status: AwardStatusEnum) => {
    if (status === AwardStatusEnum.PENDING) return false;

    if (approvePermission) {
      return status !== AwardStatusEnum.COMPLETED && status !== AwardStatusEnum.CANCELLED;
    }
    return updatePermission && status === AwardStatusEnum.DRAFT;
  };

  return (
    <>
      <PruTable
        disableBulkSelect
        title={Translation('walletAward.list')}
        headerBtnDef={[
          {
            color: 'primary',
            title: Translation('walletAward.create'),
            onClick: () => {
              history.push(`${WALLET_BASE_PATH}${WALLET_AWARD_PATH}/create`);
            },
            condition: () => createPermission,
          },
        ]}
        operationDef={[
          {
            title: Translation('section.common.operation.edit'),
            tooltipText: 'Edit Award Detail',
            onClick: (row) => {
              history.push(`${WALLET_BASE_PATH}${WALLET_AWARD_PATH}/edit/${row.awardId}`);
            },
            condition: (row) => isEditMode(row.status as AwardStatusEnum),
          },
          {
            title: Translation('section.common.operation.view'),
            tooltipText: 'View Award Detail',
            onClick: (row) => {
              history.push(`${WALLET_BASE_PATH}${WALLET_AWARD_PATH}/view/${row.awardId}`);
            },
            condition: (row) => !isEditMode(row.status as AwardStatusEnum),
          },
          {
            title: Translation('app.button.submitToApprove'),
            tooltipText: 'Submit To Approve',
            onClick: (row) => {
              onUpdatedAwardStatus?.(
                row.awardId,
                { submittedBy: user?.username ?? '' },
                AWARD_STATUS_OPERATION.SUBMIT_TO_APPROVE,
              );
            },
            condition: (row) => row.status === AwardStatusEnum.DRAFT,
          },
          {
            title: Translation('walletAward.common.status.option.active'),
            tooltipText: 'Approval Award',
            onClick: (row) => {
              // If award status is pending request post /approve api (pending --> active)
              // If award status is paused request patch /activate api to reactive award (paused --> active)
              if (row?.status === AwardStatusEnum.PENDING) {
                onUpdatedAwardStatus?.(
                  row.awardId,
                  { approvedBy: user?.username ?? '' },
                  AWARD_STATUS_OPERATION.APPROVE,
                );
              } else {
                onUpdatedAwardStatus?.(
                  row.awardId,
                  { approvedBy: user?.username ?? '' },
                  AWARD_STATUS_OPERATION.ACTIVE,
                );
              }
            },
            condition: (row) =>
              //  only display in pending status
              approvePermission &&
              row.status !== AwardStatusEnum.ACTIVE &&
              (row.status === AwardStatusEnum.PENDING ||
                row.status === AwardStatusEnum.DRAFT ||
                row.status === AwardStatusEnum.PAUSED),
          },
          {
            title: Translation('walletAward.common.status.option.pause'),
            tooltipText: 'Pause Award',
            onClick: (row) => {
              onUpdatedAwardStatus?.(row.awardId, { pausedBy: user?.username ?? '' }, AWARD_STATUS_OPERATION.PAUSE);
            },
            condition: (row) =>
              approvePermission && row.status !== AwardStatusEnum.PAUSED && row.status === AwardStatusEnum.ACTIVE,
          },
          {
            title: Translation('walletAward.common.status.option.complete'),
            tooltipText: 'Complete Award',
            onClick: (row) => {
              onUpdatedAwardStatus?.(
                row.awardId,
                { completedBy: user?.username ?? '' },
                AWARD_STATUS_OPERATION.COMPLETE,
              );
            },
            condition: (row) =>
              approvePermission && row.status !== AwardStatusEnum.COMPLETED && row.status === AwardStatusEnum.ACTIVE,
          },
          {
            title: Translation('walletAward.common.status.option.reject'),
            tooltipText: 'Reject Award',
            onClick: (row) => {
              onUpdatedAwardStatus?.(row.awardId, { rejectedBy: user?.username ?? '' }, AWARD_STATUS_OPERATION.REJECT);
            },
            condition: (row) => approvePermission && row.status === AwardStatusEnum.PENDING,
          },
          {
            title: Translation('walletAward.common.status.option.cancel'),
            tooltipText: 'Cancel Award',
            onClick: (row) => {
              onUpdatedAwardStatus?.(row.awardId, { cancelledBy: user?.username ?? '' }, AWARD_STATUS_OPERATION.CANCEL);
            },
            condition: (row) =>
              approvePermission &&
              row.status !== AwardStatusEnum.CANCELLED &&
              (row.status === AwardStatusEnum.DRAFT ||
                row.status === AwardStatusEnum.ACTIVE ||
                row.status === AwardStatusEnum.PAUSED),
          },
          {
            title: Translation('walletAward.viewAssignedAward'),
            tooltipText: 'View Assigned Award',
            onClick: (row) =>
              history.push(`${WALLET_BASE_PATH}${WALLET_ASSIGNED_AWARD_PATH}/list?awardId=${row.awardId}`),
            condition: () => viewAssignedPermission,
          },
        ]}
        columnDef={[
          {
            isId: true,
            keyIndex: 'awardId',
            align: 'center',
            displayName: Translation('walletAward.common.awardId'),
            renderData: (row) => String(row.awardId ? row.awardId : ''),
          },
          {
            keyIndex: 'awardNameZh',
            align: 'center',
            displayName: Translation('walletAward.common.awardNameZh'),
            renderData: (row) => String(row.awardNameZh ? row.awardNameZh : ''),
          },
          {
            keyIndex: 'awardNameEn',
            align: 'center',
            displayName: Translation('walletAward.common.awardNameEn'),
            renderData: (row) => String(row.awardNameEn ? row.awardNameEn : ''),
          },
          {
            keyIndex: 'awardValidityStartDate',
            align: 'center',
            displayName: Translation('walletAward.common.awardValidityFrom'),
            renderData: (row) =>
              row.awardValidityStartDate ? getDefaultDisplayDate(row.awardValidityStartDate, 'date') : '',
          },
          {
            keyIndex: 'awardValidityEndDate',
            align: 'center',
            displayName: Translation('walletAward.common.awardValidityTo'),
            renderData: (row) =>
              row.awardValidityEndDate ? getDefaultDisplayDate(row.awardValidityEndDate, 'date') : '',
          },
          {
            keyIndex: 'remainingQuantity',
            align: 'center',
            displayName: Translation('walletAward.common.remainingQuantity'),
            renderData: (row) => String(row.remainingQuantity ? row.remainingQuantity : ''),
          },
          {
            keyIndex: 'status',
            align: 'center',
            displayName: Translation('walletAward.common.status'),
            renderData: (row) => Translation(`walletAward.common.status.option.${row.status}`),
          },
        ]}
        isLoading={isLoading}
        onRefresh={onRefresh}
        dataSource={listRes?.data?.items}
        totalPages={listRes?.data?.totalPages}
        totalRecords={listRes?.data?.totalCount}
        onChangePage={onChangePage}
      />
    </>
  );
};

export default WalletAwardList;
